/**
 * Tenants API
 */

import { Tenant, TenantCreate } from '../types/api';
import { getAPIClient } from './client';

export async function listTenants(): Promise<Tenant[]> {
  const client = getAPIClient();
  return client.get<Tenant[]>('/tenants');
}

export async function getTenant(tenantId: string): Promise<Tenant> {
  const client = getAPIClient();
  return client.get<Tenant>(`/tenants/${tenantId}`);
}

export async function createTenant(data: TenantCreate): Promise<Tenant> {
  const client = getAPIClient();
  return client.post<Tenant>('/tenants', data);
}

export async function updateTenant(tenantId: string, data: Partial<TenantCreate>): Promise<Tenant> {
  const client = getAPIClient();
  return client.patch<Tenant>(`/tenants/${tenantId}`, data);
}

// ============================================================================
// EMAIL CONFIGURATION (SendGrid only - theme from tenant ThemeDefinition)
// ============================================================================

export interface EmailConfig {
  enabled: boolean;
  fromEmail: string;
  fromName: string;
  baseUrl: string;
  sendgridConfigured: boolean;
  sendgridBackupConfigured: boolean;
  sendgridUseBackup: boolean;
  // Note: Theme colors come from tenant.configuration['theme'] or ['customization']['theme']
  // and are automatically used in email templates
}

export interface EmailConfigUpdateRequest {
  enabled?: boolean;
  fromEmail?: string;
  fromName?: string;
  sendgridApiKey?: string; // Leave blank to keep existing
  sendgridBackupApiKey?: string; // Leave blank to keep existing
  sendgridUseBackup?: boolean;
}

export interface TestEmailRequest {
  toEmail: string;
}

export interface TestEmailResponse {
  success: boolean;
  message: string;
}

export async function getTenantEmailConfig(tenantId: string): Promise<EmailConfig> {
  const client = getAPIClient();
  const data = await client.get<any>(`/tenants/${tenantId}/email-config`);
  return {
    enabled: data.enabled,
    fromEmail: data.from_email,
    fromName: data.from_name,
    baseUrl: data.base_url,
    sendgridConfigured: data.sendgrid_configured,
    sendgridBackupConfigured: data.sendgrid_backup_configured,
    sendgridUseBackup: data.sendgrid_use_backup,
  };
}

export async function updateTenantEmailConfig(
  tenantId: string,
  data: EmailConfigUpdateRequest
): Promise<EmailConfig> {
  const client = getAPIClient();
  const requestData: any = {};
  if (data.enabled !== undefined) requestData.enabled = data.enabled;
  if (data.fromEmail !== undefined) requestData.from_email = data.fromEmail;
  if (data.fromName !== undefined) requestData.from_name = data.fromName;
  if (data.sendgridApiKey !== undefined && data.sendgridApiKey.trim()) {
    requestData.sendgrid_api_key = data.sendgridApiKey;
  }
  if (data.sendgridBackupApiKey !== undefined && data.sendgridBackupApiKey.trim()) {
    requestData.sendgrid_backup_api_key = data.sendgridBackupApiKey;
  }
  if (data.sendgridUseBackup !== undefined) {
    requestData.sendgrid_use_backup = data.sendgridUseBackup;
  }
  const response = await client.put<any>(`/tenants/${tenantId}/email-config`, requestData);
  return {
    enabled: response.enabled,
    fromEmail: response.from_email,
    fromName: response.from_name,
    baseUrl: response.base_url,
    sendgridConfigured: response.sendgrid_configured,
    sendgridBackupConfigured: response.sendgrid_backup_configured,
    sendgridUseBackup: response.sendgrid_use_backup,
  };
}

export async function testTenantEmail(
  tenantId: string,
  request: TestEmailRequest
): Promise<TestEmailResponse> {
  const client = getAPIClient();
  return client.post<TestEmailResponse>(`/tenants/${tenantId}/email-config/test`, {
    to_email: request.toEmail,
  });
}


